import { ADMIN_ROLE_ID, STAFF_ROLE_ID } from '@allurion/domain';
import { Card } from '@allurion/ui';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { TrackedButton, TrackedPageHeader } from 'src/analytics/TrackedUI';
import { getProviderByEmail } from 'src/api/ProviderApi';
import { Loader } from 'src/components/ui/Loader';
import { toastSuccess, toastError } from 'src/components/ui/toasts';
import { useAppNavigate } from 'src/hooks/useAppNavigate';
import { Logger } from 'src/services/Logger';

import translations from './ManageUsers.translations';

import styles from './ResendInvitationPage.module.scss';

export function ResendInvitationPage() {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { toClinicProvidersPage } = useAppNavigate();
  const user = (location.state as any)?.user;

  const email = user?.EmailAddress;
  const firstName = user?.FirstName;
  const lastName = user?.LastName;
  const clinicID = user?.ClinicID;
  const locationClinicLabel = user?.ClinicLabel;

  const intl = useIntl();
  const adminLabel = intl.formatMessage(translations.manageUsersAdmin);
  const staffLabel = intl.formatMessage(translations.manageUsersStaff);

  const roleMap = {
    2: { label: adminLabel, value: ADMIN_ROLE_ID },
    3: { label: staffLabel, value: STAFF_ROLE_ID },
  };
  const roleID: keyof typeof roleMap = user?.RoleID.toString();
  const role = roleMap[roleID];
  const clinicName = locationClinicLabel.split('\n').join(', ');

  const handleResendInvite = async () => {
    setIsLoading(true);
    try {
      const provider = await getProviderByEmail(email, clinicID[0]);
      const username = provider?.CognitoUserName;

      await Auth.resendSignUp(username);
      toastSuccess(intl.formatMessage(translations.resendInvitationResendSuccess));
      toClinicProvidersPage(clinicID);
    } catch (error) {
      Logger.captureException(error);
      toastError(intl.formatMessage(translations.resendInvitationResendFailure));
    }
    setIsLoading(false);
  };

  const handleCloseBtn = () => {
    toClinicProvidersPage(clinicID);
  };

  const listItems = [
    {
      label: intl.formatMessage(translations.resendInvitationNameAndLastName),
      value: `${firstName} ${lastName}`,
    },
    { label: intl.formatMessage(translations.resendInvitationClinic), value: clinicName },
    { label: intl.formatMessage(translations.resendInvitationEmail), value: email },
    { label: intl.formatMessage(translations.resendInvitationRole), value: role.label },
  ];

  const btnsList = [
    {
      label: intl.formatMessage(translations.resendInvitationClose),
      onClick: handleCloseBtn,
    },
    {
      show: true,
      label: intl.formatMessage(translations.resendInvitationResendInvite),
      onClick: handleResendInvite,
    },
  ];

  const title = translations.resendInvitationTitle;

  return (
    <>
      <TrackedPageHeader
        title={intl.formatMessage({
          id: 'resend-invitation.header',
          defaultMessage: 'Resend Invitation',
          description: 'Resend Invitation page header',
        })}
        onNavButtonClick={() => toClinicProvidersPage(clinicID)}
        onNavButtonClickTrackLabel="back-to-clinic-providers"
      />
      <div className={styles.container}>
        <Card>
          <p className={styles.title}>{intl.formatMessage(title)}</p>
          <ul className={styles.list}>
            {listItems.map(({ label, value }) => (
              <li key={`resend-invitation_${label}`}>
                <div className={styles.label}>{`${label}:`}</div>
                <div className={styles.value}>{value}</div>
              </li>
            ))}
          </ul>

          <div className={styles.controls}>
            {btnsList.map(
              ({ show, label, onClick }) =>
                show && (
                  <TrackedButton
                    key={`resend-invitation_${label}`}
                    disabled={isLoading}
                    label={label}
                    onClick={onClick}
                    trackLabel="resend-invitation"
                  />
                )
            )}
          </div>
        </Card>
        <Loader isLoading={isLoading} />
      </div>
    </>
  );
}
